// Bem-Estar > enviar para o Planner (0.43), direto, sem abrir a ficha de tarefa.
// Se já houver algo no mesmo dia e horário, pergunta: Substituir, Manter ambas ou Cancelar.
import { Alert } from 'react-native';
import { getDoc } from '../store/store';
import { deleteTask, newTask, saveTask, tasksOn } from '../store/planner';
import { planoDoDia } from '../store/bemestar';
import type { Cuidado, Item, Refeicao, Suplemento, Task, Treino } from '../store/types';
import { addDays, horaTela, parseYmd, today, uid } from './date';
import { t } from './i18n';
import { qtdTxt } from '../components/bemestar/EscolherAlimento';
import { nomeEx } from '../components/bemestar/Treinos';

const itemTxt = (i: Item) => [qtdTxt(i), i.text].filter(Boolean).join(' ');
const recurDe = (dias: number[]) => (dias.length === 7 ? { type: 'daily' as const } : { type: 'weekly' as const, days: dias });
const base = (p: Partial<Task>): Task => newTask({ area: 'bemestar', energy: 0, emojiManual: true, alerts: { mode: 'start', before: [] }, ...p });

/** Refeição numa data (o cardápio daquele dia) ou toda semana (pelos dias dela). */
export const tarefaRefeicao = (r: Refeicao, data?: string) => base({
  title: r.nome, emoji: r.icon, time: r.hora, duration: 30, start: data ?? today(), recur: data ? { type: 'none' } : recurDe(r.dias),
  subtasks: (data ? planoDoDia(r, data) : r.itens).map(i => ({ id: uid(), text: itemTxt(i) })), link: { aba: 'bemestar', id: r.id, label: r.nome },
});
export const tarefaTreino = (x: Treino, data?: string) => base({
  title: x.nome, emoji: x.icon, time: x.hora, duration: Math.max(20, x.exercicios.reduce((s, e) => s + (e.min ?? 0) + ((e.series ?? 0) * 1.5), 0) || 45),
  start: data ?? today(), recur: data ? { type: 'none' } : recurDe(x.dias), energy: 2,
  subtasks: x.exercicios.map(e => ({ id: uid(), text: nomeEx(e) })), link: { aba: 'bemestar', id: x.id, label: x.nome },
});
export const tarefaCuidado = (c: Cuidado, data?: string) => base({
  title: c.nome, emoji: c.icon, time: c.hora, duration: Math.max(5, c.passos.reduce((s, p) => s + p.min, 0)),
  start: data ?? today(), recur: data ? { type: 'none' } : recurDe(c.dias),
  subtasks: c.passos.map(p => ({ id: uid(), text: p.texto })), link: { aba: 'bemestar', id: c.id, label: c.nome },
});
export const tarefaSuplemento = (s: Suplemento, data?: string) => base({
  title: s.dose ? `${s.nome} (${s.dose})` : s.nome, emoji: s.icon, time: s.hora, duration: 5, insistente: false,
  start: data ?? today(), recur: data ? { type: 'none' } : recurDe(s.dias), link: { aba: 'bemestar', id: s.id, label: s.nome },
});

/** Tarefas que já estão no mesmo dia e horário (olhando os próximos 7 dias para as que se repetem). */
function conflitos(nova: Task): { t: Task; data: string }[] {
  if (!nova.time) return [];
  const tarefas = getDoc('planner').tasks;
  const out: { t: Task; data: string }[] = [];
  const n = nova.recur.type === 'none' ? 1 : 7;
  for (let i = 0; i < n; i++) {
    const data = addDays(nova.start, i);
    const dow = parseYmd(data).getDay();
    if (nova.recur.type === 'weekly' && !(nova.recur.days ?? []).includes(dow)) continue;
    for (const x of tasksOn(data, tarefas)) if (x.time === nova.time) out.push({ t: x, data });
  }
  return out;
}

const perguntar = (nova: Task, c: { t: Task; data: string }[]) => new Promise<'sub' | 'ambas' | 'cancelar'>(ok => {
  const nomes = [...new Set(c.map(x => x.t.title))].slice(0, 3).join(', ');
  Alert.alert(t('Horário ocupado'), t('"{nova}" às {hora} coincide com: {outras}.', { nova: nova.title, hora: horaTela(nova.time), outras: nomes }), [
    { text: t('Cancelar'), style: 'cancel', onPress: () => ok('cancelar') },
    { text: t('Manter ambas'), onPress: () => ok('ambas') },
    { text: t('Substituir'), style: 'destructive', onPress: () => ok('sub') },
  ], { cancelable: true, onDismiss: () => ok('cancelar') });
});

/** Envia as tarefas para o Planner, perguntando quando o horário já estiver ocupado. Devolve quantas entraram. */
export async function enviarParaPlanner(novas: Task[]): Promise<number> {
  let n = 0;
  for (const nova of novas) {
    const c = conflitos(nova);
    if (c.length) {
      const r = await perguntar(nova, c);
      if (r === 'cancelar') continue;
      if (r === 'sub') for (const x of c) deleteTask(x.t.id, x.t.recur.type === 'none' ? 'all' : 'one', x.data);
    }
    saveTask(nova); n++;
  }
  return n;
}

export async function enviarComAviso(novas: Task[]) {
  if (!novas.length) { Alert.alert(t('Nada para enviar.')); return; }
  const n = await enviarParaPlanner(novas);
  if (n) Alert.alert('✅ ' + t('Enviado para o Planner'), t('{n} item(ns) no Planner.', { n }));
}
